import { buildAhaResult } from '@fruitful/money-map-simulator';
import {
    type CmmArtifactFinalPageExperiment_ExperimentParam_Value,
    type Guide,
    type OnboardingChatResults,
    type OnboardingChatResultsTooltipData,
    type OnboardingChatResultsTooltipType,
} from '@fruitful/types';
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import type { ConversationalMMTrackEvent } from '../analytics/events';
import { getResultsSlides } from '../components/constants';
import { type TerminalSlideAction } from '../types';
import {
    ConversationalMMContext,
    type ConversationalMMContextValue,
} from './ConversationalMMContextBase';

export { useConversationalMMContext } from './ConversationalMMContextBase';

const noopTrackEvent: ConversationalMMTrackEvent = () => {};

export interface ConversationalMMProviderProps {
    children: ReactNode;
    results: OnboardingChatResults;
    onResultsChange?: (results: OnboardingChatResults) => void;
    terminalAction: TerminalSlideAction;
    showGuidedTooltips?: boolean;
    hasSeenGuidedTooltips?: boolean;
    onGuidedTooltipsSeen?: () => void;
    onTrackEvent?: ConversationalMMTrackEvent;
    guides?: Guide[];
    cmmFinalArtifactSegment?: CmmArtifactFinalPageExperiment_ExperimentParam_Value;
    /** Optional render prop for account creation form on the Summary step. */
    renderAccountForm?: () => ReactNode;
}

export const ConversationalMMProvider = ({
    children,
    results: resultsProp,
    onResultsChange,
    terminalAction,
    showGuidedTooltips = false,
    hasSeenGuidedTooltips = false,
    onGuidedTooltipsSeen,
    onTrackEvent = noopTrackEvent,
    guides = [],
    cmmFinalArtifactSegment,
    renderAccountForm,
}: ConversationalMMProviderProps) => {
    const [results, setResults] = useState(resultsProp);
    const [slideIndex, setSlideIndexState] = useState(0);

    // Tracks the last value *we* pushed out via onResultsChange, so the
    // reset effect below can tell "the caller handed us a genuinely
    // different results object" (e.g. grove switching threads) apart from
    // "our own edit just echoed back through the caller's store" (e.g.
    // member-app mirroring slider drags into its jotai atom). Without this
    // guard, every local edit round-trips back down as a "new" resultsProp
    // and gets reset here, racing the next edit and flickering between
    // stale/fresh values.
    const lastEmittedResultsRef = useRef<OnboardingChatResults | null>(null);

    useEffect(() => {
        if (resultsProp === lastEmittedResultsRef.current) {
            return;
        }
        setResults(resultsProp);
        setSlideIndexState(0);
    }, [resultsProp]);

    useEffect(() => {
        lastEmittedResultsRef.current = results;
        onResultsChange?.(results);
    }, [results, onResultsChange]);

    const ahaResult = useMemo(() => buildAhaResult(results), [results]);

    const setSlideIndex = useCallback((index: number) => {
        setSlideIndexState(index);
    }, []);

    const { slides, slideCount, lastIndex } = useMemo(() => {
        const resolvedSlides = getResultsSlides(results.rawChatResults?.branch);
        return {
            slides: resolvedSlides,
            slideCount: resolvedSlides.length,
            lastIndex: resolvedSlides.length - 1,
        };
    }, [results.rawChatResults?.branch]);

    const [tooltipSequence, setTooltipSequence] = useState<
        OnboardingChatResultsTooltipType[]
    >([]);
    const [activeTooltip, setActiveTooltip] =
        useState<OnboardingChatResultsTooltipType | null>(null);
    const [tooltipData, setTooltipData] =
        useState<OnboardingChatResultsTooltipData | null>(null);

    const value = useMemo<ConversationalMMContextValue>(
        () => ({
            results,
            setResults,
            ahaResult,
            slideIndex,
            setSlideIndex,
            slides,
            slideCount,
            lastIndex,
            tooltipSequence,
            setTooltipSequence,
            activeTooltip,
            setActiveTooltip,
            tooltipData,
            setTooltipData,
            showGuidedTooltips,
            hasSeenGuidedTooltips,
            onGuidedTooltipsSeen: onGuidedTooltipsSeen ?? (() => {}),
            onTrackEvent,
            terminalAction,
            guides,
            cmmFinalArtifactSegment,
            renderAccountForm,
        }),
        [
            results,
            ahaResult,
            slideIndex,
            setSlideIndex,
            slides,
            slideCount,
            lastIndex,
            tooltipSequence,
            activeTooltip,
            tooltipData,
            showGuidedTooltips,
            hasSeenGuidedTooltips,
            onGuidedTooltipsSeen,
            onTrackEvent,
            terminalAction,
            guides,
            cmmFinalArtifactSegment,
            renderAccountForm,
        ],
    );

    return (
        <ConversationalMMContext.Provider value={value}>
            {children}
        </ConversationalMMContext.Provider>
    );
};
